import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { Separator } from "@/components/ui/Separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { useGetAuditlogOfTaskQuery } from "@/services/TasksApi";
import { displayDate } from "@/utils/Helper";
import React from "react";
import { TaskPriority, TaskStatus } from "@/app/types/tasks";

interface TicketDetails {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  ticketId?: number;
}
const taskStatusMap: Record<string, string> = {
  [TaskStatus.TODO]: "Pending",
  [TaskStatus.IN_PROGRESS]: "In-Progress",
  [TaskStatus.HOLD]: "Hold",
  [TaskStatus.COMPLETED]: "Completed",
  [TaskStatus.ARCHIVED]: "Archived",
};

const taskPriorityMap: Record<string, string> = {
  [TaskPriority.LOW]: "Low",
  [TaskPriority.MEDIUM]: "Medium",
  [TaskPriority.HIGH]: "High",
};
const formatValue = (value: string | any[]) => {
  if (Array.isArray(value) && value.length > 0) return value.join(", ");
  if (!value || value === "()" || value === "[]" || value === "")
    return "No Data";

  return typeof value === "string"
    ? taskStatusMap[value] || taskPriorityMap[value] || value
    : value;
};


const TaskLogs: React.FC<TicketDetails> = ({ isOpen, setOpen, ticketId }) => {
  const { data, isLoading, error, refetch } = useGetAuditlogOfTaskQuery(
    { taskId: ticketId?.toString() ?? "0" },
    {
      skip: !isOpen || ticketId === undefined,
    }
  );

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (open) refetch();
  };

  if (isLoading && isOpen) {
    return (
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent className="!h-screen !max-w-[900px] overflow-auto fancy-scrollbar">
          <SheetHeader>
            <SheetTitle>Loading...</SheetTitle>
          </SheetHeader>
        </SheetContent>
      </Sheet>
    );
  }

  if (error) return <div>Error loading ticket Logs</div>;

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="!h-screen !max-w-[900px] overflow-auto fancy-scroll">
        <Card className="rounded-lg border mt-7">
          <CardHeader className="px-4 py-3 border-b">
            <h2 className="text-lg font-medium">History & Logs</h2>
          </CardHeader>
          <CardContent className="max-w-[850px] overflow-auto fancy-scroll ">
            {data?.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No logs found. 😶
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-nowrap">Type</TableHead>
                    <TableHead className="text-nowrap">Field Name</TableHead>
                    <TableHead className="text-nowrap">Updated From</TableHead>
                    <TableHead className="text-nowrap">Updated To</TableHead>
                    <TableHead className="text-nowrap">Action Date</TableHead>
                    <TableHead className="text-nowrap">Action By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.map((log, index) => (
                    <TableRow key={index}>
                      <TableCell className="capitalize text-nowrap">
                        {log.type}
                      </TableCell>
                      <TableCell className="capitalize text-nowrap">
                        {log.field_name}
                      </TableCell>
                      <TableCell className="capitalize text-nowrap">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                {" "}
                                {formatValue(log.updated_from ?? "")}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {" "}
                              {formatValue(log.updated_from ?? "")}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="capitalize text-nowrap">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span> {formatValue(log.updated_to ?? "")}</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {" "}
                              {formatValue(log.updated_to ?? "")}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="capitalize text-nowrap">
                        {new Date(log.action_date)
                        .toLocaleDateString("en-PK", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                        .replace(/\//g, "-")}
                      </TableCell>
                      <TableCell className="capitalize text-nowrap">
                        {log.action_by}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </SheetContent>
    </Sheet>
  );
};

export default TaskLogs;
